import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import axios from "axios";
import Topbar from "../component/Topbar";
import Sidebar from "../component/Sidebar";
import Footer from "../component/Footer";
import Loader from "../component/Loader";
import MessageToster from "../component/MessageToster";
import { useUser } from "../context/UserContext";
import { BASE_URL } from "../api";
import { getApiHeaders } from "../utils/helpers";
import { useHandleError } from "../hooks/useHandleError";

export default function BecomeIb() {
  const navigate = useNavigate();
  const handleError = useHandleError();
  const { userData: contextUserData, fetchUserData } = useUser();
  const cookieUserData = JSON.parse(Cookies.get("userdata") || "{}");
  const userData = contextUserData || cookieUserData;

  const rawStatus = String(userData?.ibStatus || "").toLowerCase().trim();
  const isIbActive = Number(userData?.isIb) === 1 || ["active", "approve", "approved"].includes(rawStatus);
  const isIbPending = rawStatus === "pending";
  const isIbRejected = ["reject", "rejected"].includes(rawStatus);

  const [isReapplying, setIsReapplying] = useState(false);
  const [ibLoading, setIbLoading] = useState(false);
  const [beforeReg, setBeforeReg] = useState(false);
  const [ibTimer, setIbTimer] = useState(0);
  const [isSubmittedSuccess, setIsSubmittedSuccess] = useState(false);

  const [ibData, setIbData] = useState({
    email: userData?.email || "",
    fname: userData?.fname || "",
    lname: userData?.lname || "",
    yearofexp: "",
    companyname: "",
    existclients: "",
    avgvolume: "",
    verifycode: "",
  });

  const [ibErrors, setIbErrors] = useState({});

  useEffect(() => {
    if (userData?.email && !ibData.email) {
      setIbData((prev) => ({
        ...prev,
        email: userData.email || "",
        fname: prev.fname || userData.fname || "",
        lname: prev.lname || userData.lname || "",
      }));
    }
  }, [userData]);

  useEffect(() => {
    if (typeof fetchUserData === "function") {
      fetchUserData();
    }
  }, []);

  useEffect(() => {
    let interval = null;
    if (ibTimer > 0) {
      interval = setInterval(() => {
        setIbTimer((prev) => prev - 1);
      }, 1000);
    } else {
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [ibTimer]);

  const formatTimer = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  const blockInvalidNumberKeys = (e) => {
    if (["e", "E", "+", "-", "."].includes(e.key)) {
      e.preventDefault();
    }
  };

  const blockInvalidDecimalKeys = (e) => {
    if (["e", "E", "+", "-"].includes(e.key)) {
      e.preventDefault();
    }
  };

  const handleIbChange = (e) => {
    const { name, value } = e.target;

    if (name === "avgvolume") {
      if (value !== "" && !/^\d*\.?\d*$/.test(value)) return;
    } else if (["yearofexp", "existclients", "verifycode"].includes(name)) {
      if (value !== "" && !/^\d+$/.test(value)) return;
    }

    setIbData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setIbErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const resetIbForm = () => {
    setIbData({
      email: userData?.email || "",
      fname: userData?.fname || "",
      lname: userData?.lname || "",
      yearofexp: "",
      companyname: "",
      existclients: "",
      avgvolume: "",
      verifycode: "",
    });
    setIbErrors({});
    setBeforeReg(false);
    setIbTimer(0);
  };

  const validateForm = () => {
    const errors = {};

    // Email validation
    if (!ibData.email || ibData.email.trim() === "") {
      errors.email = "Email is required";
    } else if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(ibData.email)) {
      errors.email = "Enter a valid email address";
    }

    // First Name validation
    if (!ibData.fname || ibData.fname.trim() === "") {
      errors.fname = "First name is required";
    } else if (ibData.fname.trim().length < 2) {
      errors.fname = "First name must be at least 2 characters";
    } else if (!/^[a-zA-Z\s.-]+$/.test(ibData.fname)) {
      errors.fname = "First name must contain only letters";
    }

    // Last Name validation
    if (!ibData.lname || ibData.lname.trim() === "") {
      errors.lname = "Last name is required";
    } else if (ibData.lname.trim().length < 2) {
      errors.lname = "Last name must be at least 2 characters";
    } else if (!/^[a-zA-Z\s.-]+$/.test(ibData.lname)) {
      errors.lname = "Last name must contain only letters";
    }

    // Year of Experience validation
    if (!ibData.yearofexp || ibData.yearofexp.toString().trim() === "") {
      errors.yearofexp = "Year of experience is required";
    } else {
      const exp = Number(ibData.yearofexp);
      if (isNaN(exp) || exp < 0 || exp > 80) {
        errors.yearofexp = "Enter a valid year of experience (0-80)";
      }
    }

    // Company Name validation
    if (!ibData.companyname || ibData.companyname.trim() === "") {
      errors.companyname = "Company name is required";
    } else if (ibData.companyname.trim().length < 2) {
      errors.companyname = "Company name must be at least 2 characters";
    }

    // Existing Clients validation
    if (!ibData.existclients || ibData.existclients.toString().trim() === "") {
      errors.existclients = "Existing clients count is required";
    } else {
      const clients = Number(ibData.existclients);
      if (isNaN(clients) || clients < 0) {
        errors.existclients = "Existing clients must be a non-negative number";
      }
    }

    // Average Volume validation
    if (!ibData.avgvolume || ibData.avgvolume.toString().trim() === "") {
      errors.avgvolume = "Average volume is required";
    } else {
      const vol = Number(ibData.avgvolume);
      if (isNaN(vol) || vol < 0) {
        errors.avgvolume = "Average volume must be a non-negative number";
      }
    }

    // OTP Verification validation
    if (beforeReg) {
      if (!ibData.verifycode || ibData.verifycode.toString().trim() === "") {
        errors.verifycode = "OTP is required";
      } else if (ibData.verifycode.toString().length !== 6) {
        errors.verifycode = "OTP must be exactly 6 digits";
      } else if (!/^\d+$/.test(ibData.verifycode)) {
        errors.verifycode = "OTP must contain only numbers";
      }
    }

    setIbErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const sendIbOtp = async () => {
    if (!validateForm()) return;
    try {
      setIbLoading(true);
      const body = {
        email: ibData.email,
        otype: "IBREGISTER",
      };
      const res = await axios.post(
        `${BASE_URL}/NEW_IB/before_register`,
        body,
        {
          headers: getApiHeaders(),
        }
      );
      if (res.data.code === 200) {
        MessageToster("success", res.data.message || "OTP sent successfully to your email!");
        setBeforeReg(true);
        setIbTimer(120);
      } else {
        MessageToster("error", res.data.message || "Failed to send OTP.");
      }
    } catch (error) {
      handleError(error);
    } finally {
      setIbLoading(false);
    }
  };

  const resendIbOtp = async () => {
    if (ibTimer > 0 || ibLoading) return;
    try {
      setIbLoading(true);
      const body = {
        email: ibData.email,
        otype: "IBREGISTER",
      };
      const res = await axios.post(
        `${BASE_URL}/NEW_IB/before_register`,
        body,
        {
          headers: getApiHeaders(),
        }
      );
      if (res.data.code === 200) {
        MessageToster("success", res.data.message || "OTP resent successfully!");
        setIbTimer(120);
        setIbData((prev) => ({ ...prev, verifycode: "" }));
        setIbErrors((prev) => ({ ...prev, verifycode: "" }));
      } else {
        MessageToster("error", res.data.message || "Failed to resend OTP.");
      }
    } catch (error) {
      handleError(error);
    } finally {
      setIbLoading(false);
    }
  };

  const submitIbForm = async (e) => {
    if (e) e.preventDefault();
    if (!validateForm()) return;

    try {
      setIbLoading(true);
      const bodyData = {
        email: ibData.email,
        verifycode: Number(ibData.verifycode),
        fname: ibData.fname,
        lname: ibData.lname,
        yearofexp: ibData.yearofexp,
        companyname: ibData.companyname,
        existclients: ibData.existclients,
        avgvolume: ibData.avgvolume,
      };
      const res = await axios.post(
        `${BASE_URL}/NEW_IB/register`,
        bodyData,
        {
          headers: getApiHeaders(),
        }
      );
      if (res.data.code === 200) {
        MessageToster("success", res.data.message || "IB application submitted successfully!");
        setIsSubmittedSuccess(true);
        if (typeof fetchUserData === "function") {
          fetchUserData();
        }
      } else {
        MessageToster("error", res.data.message || "IB registration failed.");
      }
    } catch (error) {
      handleError(error);
    } finally {
      setIbLoading(false);
    }
  };

  return (
    <>
      <div id="layout-wrapper">
        <Topbar />
        <Sidebar />
        <div className="main-content">
          <div className="page-content">
            <div className="container-fluid">
              {/* Page Title */}
              <div className="row">
                <div className="col-12">
                  <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                    <h2 className="fw-700 mb-sm-0">Become An IB</h2>
                  </div>
                </div>
              </div>

              {/* 1. If application was just submitted */}
              {isSubmittedSuccess ? (
                <div className="row justify-content-center mt-3 mb-5 pb-4">
                  <div className="col-xl-8 col-lg-10">
                    <div className="card shadow-sm border-0" style={{ borderRadius: "14px" }}>
                      <div className="card-body p-4 p-md-5 text-center">
                        <div
                          className="rounded-circle d-flex align-items-center justify-content-center mx-auto mb-3 shadow-sm"
                          style={{
                            width: "76px",
                            height: "76px",
                            backgroundColor: "#ecfdf5",
                            border: "2px solid #a7f3d0",
                            color: "#059669",
                          }}
                        >
                          <i className="bx bx-badge-check" style={{ fontSize: "38px" }} />
                        </div>
                        <span className="badge bg-warning-subtle text-warning border border-warning-subtle px-3 py-1 font-size-12 mb-2">
                          Status: Pending Review
                        </span>
                        <h3 className="fw-bold mb-2">Application Submitted Successfully!</h3>
                        <p className="text-muted fs-6 mb-4 mx-auto" style={{ maxWidth: "560px" }}>
                          Thank you for applying to become an Introducing Broker. Your application has been submitted and is currently being reviewed by our team.
                        </p>
                        <div className="d-flex justify-content-center gap-3 flex-wrap">
                          <Link to="/dashboard" className="btn btn-primary px-4 py-2">
                            <i className="bx bx-home me-1" /> Back to Dashboard
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : isIbActive && !isReapplying ? (
                /* 2. Active / Approved IB User */
                <div className="row justify-content-center mt-3 mb-5 pb-4">
                  <div className="col-xl-8 col-lg-10">
                    <div className="card shadow-sm border-0 overflow-hidden" style={{ borderRadius: "14px" }}>
                      <div className="card-body p-4 p-md-5 text-center">
                        <div
                          className="rounded-circle d-flex align-items-center justify-content-center mx-auto mb-3 shadow-sm"
                          style={{
                            width: "76px",
                            height: "76px",
                            backgroundColor: "#ecfdf5",
                            border: "2px solid #a7f3d0",
                            color: "#059669",
                          }}
                        >
                          <i className="bx bx-check-shield" style={{ fontSize: "38px" }} />
                        </div>
                        <span className="badge bg-success-subtle text-success border border-success-subtle px-3 py-1 font-size-12 mb-2">
                          Status: Active IB
                        </span>
                        <h3 className="fw-bold mb-2">You are an Introducing Broker (IB)!</h3>
                        <p className="text-muted fs-6 mb-4 mx-auto" style={{ maxWidth: "560px" }}>
                          Your IB account is active and verified. You have full access to the IB Dashboard, client reports, commission analytics, and withdrawals.
                        </p>
                        <div className="d-flex justify-content-center gap-3 flex-wrap">
                          <Link to="/ib_dashboard" className="btn btn-primary px-4 py-2">
                            <i className="bx bx-tachometer me-1" /> Go to IB Dashboard
                          </Link>
                          <Link to="/report" className="btn btn-outline-secondary px-4 py-2">
                            <i className="bx bx-file me-1" /> View IB Reports
                          </Link>
                          <Link to="/ib-income" className="btn btn-outline-primary px-4 py-2">
                            <i className="bx bx-wallet me-1" /> IB Income
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : isIbPending && !isReapplying ? (
                /* 3. Pending Application */
                <div className="row justify-content-center mt-3 mb-5 pb-4">
                  <div className="col-xl-8 col-lg-10">
                    <div className="card shadow-sm border-0" style={{ borderRadius: "14px" }}>
                      <div className="card-body p-4 p-md-5 text-center">
                        <div
                          className="rounded-circle d-flex align-items-center justify-content-center mx-auto mb-3 shadow-sm"
                          style={{
                            width: "76px",
                            height: "76px",
                            backgroundColor: "#fffbeb",
                            border: "2px solid #fde68a",
                            color: "#d97706",
                          }}
                        >
                          <i className="bx bx-time-five" style={{ fontSize: "38px" }} />
                        </div>
                        <span className="badge bg-warning-subtle text-warning border border-warning-subtle px-3 py-1 font-size-12 mb-2">
                          Status: Pending Review
                        </span>
                        <h3 className="fw-bold mb-2">Your IB Application is Under Review</h3>
                        <p className="text-muted fs-6 mb-4 mx-auto" style={{ maxWidth: "560px" }}>
                          Your application to become an Introducing Broker has been received and is currently being verified by our compliance team. We will activate your account once verified.
                        </p>

                        <div className="card bg-light border-0 mx-auto mb-4 text-start" style={{ maxWidth: "460px", borderRadius: "10px" }}>
                          <div className="card-body p-3">
                            <div className="d-flex justify-content-between py-1 border-bottom">
                              <span className="text-muted font-size-13">Registered Email:</span>
                              <span className="fw-semibold font-size-13">{userData?.email || "-"}</span>
                            </div>
                            <div className="d-flex justify-content-between py-1 pt-2">
                              <span className="text-muted font-size-13">Application Status:</span>
                              <span className="badge bg-warning text-dark font-size-12">Pending Approval</span>
                            </div>
                          </div>
                        </div>

                        <div className="d-flex justify-content-center gap-3 flex-wrap">
                          <button
                            type="button"
                            className="btn btn-outline-primary px-4 py-2"
                            onClick={() => {
                              if (typeof fetchUserData === "function") fetchUserData();
                              MessageToster("info", "Checking application status...");
                            }}
                          >
                            <i className="bx bx-refresh me-1" /> Refresh Status
                          </button>
                          <Link to="/dashboard" className="btn btn-light px-4 py-2 border">
                            <i className="bx bx-home me-1" /> Back to Dashboard
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : isIbRejected && !isReapplying ? (
                /* 4. Rejected Application */
                <div className="row justify-content-center mt-3 mb-5 pb-4">
                  <div className="col-xl-8 col-lg-10">
                    <div className="card shadow-sm border-0" style={{ borderRadius: "14px" }}>
                      <div className="card-body p-4 p-md-5 text-center">
                        <div
                          className="rounded-circle d-flex align-items-center justify-content-center mx-auto mb-3 shadow-sm"
                          style={{
                            width: "76px",
                            height: "76px",
                            backgroundColor: "#fef2f2",
                            border: "2px solid #fecaca",
                            color: "#dc2626",
                          }}
                        >
                          <i className="bx bx-x-circle" style={{ fontSize: "38px" }} />
                        </div>
                        <span className="badge bg-danger-subtle text-danger border border-danger-subtle px-3 py-1 font-size-12 mb-2">
                          Status: Application Rejected
                        </span>
                        <h3 className="fw-bold mb-2">IB Application Not Approved</h3>
                        <p className="text-muted fs-6 mb-4 mx-auto" style={{ maxWidth: "560px" }}>
                          Unfortunately, your previous IB application was not approved. You can review your details and submit a new application.
                        </p>
                        <div className="d-flex justify-content-center gap-3 flex-wrap">
                          <button
                            type="button"
                            className="btn btn-primary px-4 py-2"
                            onClick={() => setIsReapplying(true)}
                          >
                            <i className="bx bx-edit me-1" /> Re-Apply for IB
                          </button>
                          <Link to="/tickets" className="btn btn-outline-secondary px-4 py-2">
                            <i className="bx bx-support me-1" /> Contact Support
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                /* 5. Registration Form (not_applied or re-applying) */
                <div className="row justify-content-center mt-2 mb-5 pb-4">
                  <div className="col-xl-11 col-xxl-10">
                    <div className="row">
                      {/* Left Info Column - Simple, Clean & Project-Aligned */}
                      <div className="col-xl-4 col-lg-5 mb-4 mb-lg-0">
                        <div
                          className="card shadow-sm border-0 h-100 mb-3 mb-lg-0"
                          style={{
                            borderRadius: "14px",
                            background: "linear-gradient(180deg, #f8faff 0%, #ffffff 100%)",
                            border: "1px solid #e9edf2",
                          }}
                        >
                          <div className="card-body p-4 d-flex flex-column">
                            <div className="text-center mb-2">
                              <img
                                src="assets/images/broker.png"
                                alt="Become Broker"
                                className="img-fluid mb-3"
                                style={{ maxHeight: "135px", objectFit: "contain" }}
                              />
                              <h4 className="fw-bold text-dark mb-1">Introducing Broker</h4>
                              <p className="text-muted font-size-13 mb-0">
                                Earn commissions by referring clients
                              </p>
                            </div>

                            <hr className="my-3 text-muted opacity-25" />

                            {/* Simple points according to project */}
                            <div className="mb-3">
                              <h6 className="text-uppercase text-muted font-size-11 fw-bold tracking-wider mb-3">
                                IB Features
                              </h6>

                              <div className="d-flex align-items-center mb-3">
                                <div
                                  className="avatar-xs bg-primary bg-opacity-10 text-primary rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0"
                                  style={{ width: "34px", height: "34px" }}
                                >
                                  <i className="bx bx-dollar-circle font-size-18" />
                                </div>
                                <div>
                                  <div className="fw-semibold font-size-13 text-dark">Trading Commission</div>
                                  <div className="text-muted font-size-12">Earn rebates on every client trade</div>
                                </div>
                              </div>

                              <div className="d-flex align-items-center mb-3">
                                <div
                                  className="avatar-xs bg-info bg-opacity-10 text-info rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0"
                                  style={{ width: "34px", height: "34px" }}
                                >
                                  <i className="bx bx-bar-chart-alt-2 font-size-18" />
                                </div>
                                <div>
                                  <div className="fw-semibold font-size-13 text-dark">IB Dashboard & Reports</div>
                                  <div className="text-muted font-size-12">Live volume, clients & income reports</div>
                                </div>
                              </div>

                              <div className="d-flex align-items-center mb-3">
                                <div
                                  className="avatar-xs bg-success bg-opacity-10 text-success rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0"
                                  style={{ width: "34px", height: "34px" }}
                                >
                                  <i className="bx bx-wallet font-size-18" />
                                </div>
                                <div>
                                  <div className="fw-semibold font-size-13 text-dark">Fast Withdrawals</div>
                                  <div className="text-muted font-size-12">Seamless transfer of your IB earnings</div>
                                </div>
                              </div>
                            </div>

                            <div className="mt-auto pt-3 border-top">
                              <div className="d-flex align-items-center text-muted font-size-12">
                                <i className="bx bx-shield-quarter text-primary font-size-16 me-2" />
                                <span>Email OTP verification required to apply</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Right Form Column */}
                      <div className="col-xl-8 col-lg-7">
                        <div className="card shadow-sm border-0 h-100" style={{ borderRadius: "14px" }}>
                          <div className="card-header bg-transparent border-bottom py-3 px-4 d-flex justify-content-between align-items-center">
                            <div>
                              <h5 className="card-title mb-1 fw-bold">IB Account Registration</h5>
                              <p className="text-muted small mb-0">Fill in your details to apply for an IB account.</p>
                            </div>
                            {beforeReg && (
                              <span className="badge bg-warning-subtle text-warning border border-warning-subtle px-3 py-1 font-size-12">
                                Step 2: Verify OTP
                              </span>
                            )}
                          </div>

                          <div className="card-body p-4">
                            {isReapplying && (
                              <div className="alert alert-info py-2 px-3 mb-3 d-flex align-items-center justify-content-between font-size-13">
                                <div>
                                  <i className="bx bx-info-circle me-1" />
                                  <span>Re-applying for IB: Please review/update your details and submit.</span>
                                </div>
                                <button
                                  type="button"
                                  className="btn-close"
                                  onClick={() => setIsReapplying(false)}
                                  title="Cancel Re-apply"
                                />
                              </div>
                            )}
                            <form onSubmit={(e) => { e.preventDefault(); if (beforeReg) submitIbForm(); else sendIbOtp(); }}>
                              <div className="row g-3">
                                {/* Email */}
                                <div className="col-md-12">
                                  <label className="form-label fw-semibold">
                                    Email Address <span className="text-danger">*</span>
                                  </label>
                                  <div className="input-group">
                                    <span className="input-group-text bg-light border-end-0 px-3 d-flex align-items-center justify-content-center" style={{ minWidth: "44px" }}>
                                      <i className="bx bx-envelope text-muted font-size-16" />
                                    </span>
                                    <input
                                      className={`form-control border-start-0 ps-3 ${ibErrors.email ? "is-invalid" : ""}`}
                                      type="email"
                                      placeholder="Enter your email"
                                      name="email"
                                      value={ibData.email}
                                      onChange={handleIbChange}
                                      disabled={beforeReg}
                                    />
                                  </div>
                                  {ibErrors.email && (
                                    <div className="text-danger small mt-1 d-flex align-items-center">
                                      <i className="bx bx-info-circle me-1 font-size-14" />
                                      <span>{ibErrors.email}</span>
                                    </div>
                                  )}
                                </div>

                                {/* First Name */}
                                <div className="col-md-6">
                                  <label className="form-label fw-semibold">
                                    First Name <span className="text-danger">*</span>
                                  </label>
                                  <div className="input-group">
                                    <span className="input-group-text bg-light border-end-0 px-3 d-flex align-items-center justify-content-center" style={{ minWidth: "44px" }}>
                                      <i className="bx bx-user text-muted font-size-16" />
                                    </span>
                                    <input
                                      className={`form-control border-start-0 ps-3 ${ibErrors.fname ? "is-invalid" : ""}`}
                                      type="text"
                                      placeholder="First Name"
                                      name="fname"
                                      value={ibData.fname}
                                      onChange={handleIbChange}
                                      disabled={beforeReg}
                                    />
                                  </div>
                                  {ibErrors.fname && (
                                    <div className="text-danger small mt-1 d-flex align-items-center">
                                      <i className="bx bx-info-circle me-1 font-size-14" />
                                      <span>{ibErrors.fname}</span>
                                    </div>
                                  )}
                                </div>

                                {/* Last Name */}
                                <div className="col-md-6">
                                  <label className="form-label fw-semibold">
                                    Last Name <span className="text-danger">*</span>
                                  </label>
                                  <div className="input-group">
                                    <span className="input-group-text bg-light border-end-0 px-3 d-flex align-items-center justify-content-center" style={{ minWidth: "44px" }}>
                                      <i className="bx bx-user text-muted font-size-16" />
                                    </span>
                                    <input
                                      className={`form-control border-start-0 ps-3 ${ibErrors.lname ? "is-invalid" : ""}`}
                                      type="text"
                                      placeholder="Last Name"
                                      name="lname"
                                      value={ibData.lname}
                                      onChange={handleIbChange}
                                      disabled={beforeReg}
                                    />
                                  </div>
                                  {ibErrors.lname && (
                                    <div className="text-danger small mt-1 d-flex align-items-center">
                                      <i className="bx bx-info-circle me-1 font-size-14" />
                                      <span>{ibErrors.lname}</span>
                                    </div>
                                  )}
                                </div>

                                {/* Year Of Experience */}
                                <div className="col-md-6">
                                  <label className="form-label fw-semibold">
                                    Year Of Experience <span className="text-danger">*</span>
                                  </label>
                                  <div className="input-group">
                                    <span className="input-group-text bg-light border-end-0 px-3 d-flex align-items-center justify-content-center" style={{ minWidth: "44px" }}>
                                      <i className="bx bx-time-five text-muted font-size-16" />
                                    </span>
                                    <input
                                      className={`form-control border-start-0 ps-3 ${ibErrors.yearofexp ? "is-invalid" : ""}`}
                                      type="text"
                                      inputMode="numeric"
                                      placeholder="e.g. 3"
                                      name="yearofexp"
                                      value={ibData.yearofexp}
                                      onChange={handleIbChange}
                                      onKeyDown={blockInvalidNumberKeys}
                                      disabled={beforeReg}
                                    />
                                  </div>
                                  {ibErrors.yearofexp && (
                                    <div className="text-danger small mt-1 d-flex align-items-center">
                                      <i className="bx bx-info-circle me-1 font-size-14" />
                                      <span>{ibErrors.yearofexp}</span>
                                    </div>
                                  )}
                                </div>

                                {/* Company Name */}
                                <div className="col-md-6">
                                  <label className="form-label fw-semibold">
                                    Company Name <span className="text-danger">*</span>
                                  </label>
                                  <div className="input-group">
                                    <span className="input-group-text bg-light border-end-0 px-3 d-flex align-items-center justify-content-center" style={{ minWidth: "44px" }}>
                                      <i className="bx bx-buildings text-muted font-size-16" />
                                    </span>
                                    <input
                                      className={`form-control border-start-0 ps-3 ${ibErrors.companyname ? "is-invalid" : ""}`}
                                      type="text"
                                      placeholder="Company / Agency"
                                      name="companyname"
                                      value={ibData.companyname}
                                      onChange={handleIbChange}
                                      disabled={beforeReg}
                                    />
                                  </div>
                                  {ibErrors.companyname && (
                                    <div className="text-danger small mt-1 d-flex align-items-center">
                                      <i className="bx bx-info-circle me-1 font-size-14" />
                                      <span>{ibErrors.companyname}</span>
                                    </div>
                                  )}
                                </div>

                                {/* Existing Clients */}
                                <div className="col-md-6">
                                  <label className="form-label fw-semibold">
                                    No. Of Existing Clients <span className="text-danger">*</span>
                                  </label>
                                  <div className="input-group">
                                    <span className="input-group-text bg-light border-end-0 px-3 d-flex align-items-center justify-content-center" style={{ minWidth: "44px" }}>
                                      <i className="bx bx-group text-muted font-size-16" />
                                    </span>
                                    <input
                                      className={`form-control border-start-0 ps-3 ${ibErrors.existclients ? "is-invalid" : ""}`}
                                      type="text"
                                      inputMode="numeric"
                                      placeholder="e.g. 25"
                                      name="existclients"
                                      value={ibData.existclients}
                                      onChange={handleIbChange}
                                      onKeyDown={blockInvalidNumberKeys}
                                      disabled={beforeReg}
                                    />
                                  </div>
                                  {ibErrors.existclients && (
                                    <div className="text-danger small mt-1 d-flex align-items-center">
                                      <i className="bx bx-info-circle me-1 font-size-14" />
                                      <span>{ibErrors.existclients}</span>
                                    </div>
                                  )}
                                </div>

                                {/* Average Volume Per Month */}
                                <div className="col-md-6">
                                  <label className="form-label fw-semibold">
                                    Average Volume Per Month ($) <span className="text-danger">*</span>
                                  </label>
                                  <div className="input-group">
                                    <span className="input-group-text bg-light border-end-0 px-3 d-flex align-items-center justify-content-center" style={{ minWidth: "44px" }}>
                                      <i className="bx bx-dollar text-muted font-size-16" />
                                    </span>
                                    <input
                                      className={`form-control border-start-0 ps-3 ${ibErrors.avgvolume ? "is-invalid" : ""}`}
                                      type="text"
                                      inputMode="decimal"
                                      placeholder="e.g. 50000"
                                      name="avgvolume"
                                      value={ibData.avgvolume}
                                      onChange={handleIbChange}
                                      onKeyDown={blockInvalidDecimalKeys}
                                      disabled={beforeReg}
                                    />
                                  </div>
                                  {ibErrors.avgvolume && (
                                    <div className="text-danger small mt-1 d-flex align-items-center">
                                      <i className="bx bx-info-circle me-1 font-size-14" />
                                      <span>{ibErrors.avgvolume}</span>
                                    </div>
                                  )}
                                </div>

                                {/* OTP Field (Visible only after Send OTP) */}
                                {beforeReg && (
                                  <div className="col-12 mt-3 pt-3 border-top">
                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                      <label className="form-label mb-0 fw-semibold">
                                        Verify OTP <span className="text-danger">*</span>
                                      </label>
                                      {ibTimer > 0 ? (
                                        <span className="text-muted small">
                                          Resend in <strong className="text-primary">{formatTimer(ibTimer)}</strong>
                                        </span>
                                      ) : (
                                        <button
                                          type="button"
                                          className="btn btn-link btn-sm p-0 text-decoration-none fw-semibold"
                                          onClick={resendIbOtp}
                                          disabled={ibLoading}
                                        >
                                          Resend OTP
                                        </button>
                                      )}
                                    </div>
                                    <div className="input-group">
                                      <span className="input-group-text bg-light border-end-0 px-3 d-flex align-items-center justify-content-center" style={{ minWidth: "44px" }}>
                                        <i className="bx bx-key text-muted font-size-16" />
                                      </span>
                                      <input
                                        type="text"
                                        inputMode="numeric"
                                        maxLength={6}
                                        className={`form-control border-start-0 ps-3 ${ibErrors.verifycode ? "is-invalid" : ""}`}
                                        placeholder="Enter 6-digit OTP"
                                        name="verifycode"
                                        value={ibData.verifycode}
                                        onChange={handleIbChange}
                                        onKeyDown={blockInvalidNumberKeys}
                                        autoFocus
                                      />
                                    </div>
                                    {ibErrors.verifycode && (
                                      <div className="text-danger small mt-1 d-flex align-items-center">
                                        <i className="bx bx-info-circle me-1 font-size-14" />
                                        <span>{ibErrors.verifycode}</span>
                                      </div>
                                    )}
                                  </div>
                                )}

                                {/* Action Buttons */}
                                <div className="col-12 mt-4 pt-2 d-flex justify-content-between align-items-center flex-wrap gap-2">
                                  <button
                                    type="button"
                                    className="btn btn-light border px-4 py-2"
                                    onClick={resetIbForm}
                                    disabled={ibLoading}
                                  >
                                    <i className="bx bx-revision me-1" /> Reset
                                  </button>

                                  <div className="d-flex gap-2">
                                    {!beforeReg ? (
                                      <button
                                        type="button"
                                        className="btn btn-primary px-4 py-2 fw-medium"
                                        onClick={sendIbOtp}
                                        disabled={ibLoading}
                                      >
                                        {ibLoading ? (
                                          <>
                                            <span className="spinner-border spinner-border-sm me-1" /> Sending OTP...
                                          </>
                                        ) : (
                                          <>
                                            <i className="bx bx-paper-plane me-1" /> Send OTP
                                          </>
                                        )}
                                      </button>
                                    ) : (
                                      <button
                                        type="submit"
                                        className="btn btn-primary px-4 py-2 fw-medium"
                                        disabled={ibLoading}
                                      >
                                        {ibLoading ? (
                                          <>
                                            <span className="spinner-border spinner-border-sm me-1" /> Submitting...
                                          </>
                                        ) : (
                                          <>
                                            <i className="bx bx-check me-1" /> Submit Application
                                          </>
                                        )}
                                      </button>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </form>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          <Footer />
        </div>
      </div>
    </>
  );
}
